package songwritersnotebook.app.ui.theme

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.BaselineShift
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.style.TextDecoration
import songwritersnotebook.app.viewmodels.ChordDisplayMode
import songwritersnotebook.app.viewmodels.RhymeInfo
import songwritersnotebook.app.viewmodels.RhymeType

class ChordVisualTransformation(
    private val chordBackgroundColor: Color = Color.LightGray,
    private val chordTextColor: Color = Color.Black,
    private val activeChordHighlightColor: Color = Color.Yellow,
    private val activeChordIndex: Int? = null,
    private val displayMode: ChordDisplayMode = ChordDisplayMode.Large,
    private val chordPlacement: songwritersnotebook.app.viewmodels.ChordPlacement = songwritersnotebook.app.viewmodels.ChordPlacement.Inline,
    private val rhymeData: Map<String, RhymeInfo> = emptyMap(),
    private val textColor: Color = Color.Black,
    private val endOfLineOnly: Boolean = false
) : VisualTransformation {
    private val chordRegex = Regex("\\[([^\\[\\]]+)\\]")
    private val wordRegex = Regex("[\\w']+")

    override fun filter(text: AnnotatedString): TransformedText {
        val originalText = text.text
        
        // 1. Prepare Rhyme Map for the entire line (ignoring chords)
        val plainText = originalText.replace(chordRegex, "")
        val lastWordOfLine = plainText.trim()
            .split(Regex("\\W+"))
            .lastOrNull { it.isNotBlank() }
            ?.lowercase()

        // Map from index in plainText to RhymeInfo
        val rhymeHighlightMap = mutableMapOf<Int, RhymeInfo>()
        wordRegex.findAll(plainText).forEach { match ->
            val word = match.value.lowercase()
            val info = rhymeData[word]
            if (info != null && (!endOfLineOnly || word == lastWordOfLine)) {
                for (i in match.range) {
                    rhymeHighlightMap[i] = info
                }
            }
        }

        val annotatedString = buildAnnotatedString {
            var plainTextIndex = 0
            var chordCounter = 0
            var lastOriginalIndex = 0

            // Helper to append text with rhymes
            fun appendPlainTextWithRhymes(start: Int, end: Int) {
                for (i in start until end) {
                    val char = originalText[i]
                    val info = rhymeHighlightMap[plainTextIndex]
                    if (info != null) {
                        val rhymeColor = hexToColor(info.colorHex)
                        val style = if (info.type == RhymeType.PERFECT) {
                            SpanStyle(color = rhymeColor, textDecoration = TextDecoration.Underline)
                        } else {
                            SpanStyle(color = rhymeColor)
                        }
                        withStyle(style) { append(char) }
                    } else {
                        withStyle(SpanStyle(color = textColor)) { append(char) }
                    }
                    plainTextIndex++
                }
            }

            for (match in chordRegex.findAll(originalText)) {
                // Append text BEFORE the chord
                appendPlainTextWithRhymes(lastOriginalIndex, match.range.first)

                // Append the chord itself
                if (displayMode != ChordDisplayMode.Off) {
                    if (chordPlacement == songwritersnotebook.app.viewmodels.ChordPlacement.Above) {
                        withStyle(style = SpanStyle()) {
                            append("\u200B")
                        }
                    } else {
                        val chordName = match.groupValues[1]
                        val isChordActive = chordCounter == activeChordIndex
                        val spanStyle = when (displayMode) {
                            ChordDisplayMode.Large -> SpanStyle(
                                color = if (isChordActive) Color.Black else chordTextColor,
                                background = if (isChordActive) activeChordHighlightColor else chordBackgroundColor,
                                fontWeight = FontWeight.Bold,
                                letterSpacing = 0.1.sp
                            )
                            ChordDisplayMode.Small -> SpanStyle(
                                color = if (isChordActive) activeChordHighlightColor else chordBackgroundColor,
                                fontWeight = FontWeight.Bold,
                                fontSize = 12.sp,
                                baselineShift = BaselineShift.Superscript
                            )
                            else -> SpanStyle()
                        }
                        withStyle(style = spanStyle) {
                            val visualText = if (displayMode == ChordDisplayMode.Large) "\u00A0$chordName\u00A0" else chordName
                            append(visualText)
                        }
                    }
                }
                
                lastOriginalIndex = match.range.last + 1
                chordCounter++
            }

            // Append remaining text AFTER the last chord
            if (lastOriginalIndex < originalText.length) {
                appendPlainTextWithRhymes(lastOriginalIndex, originalText.length)
            }
        }

        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                var transformedOffset = 0
                var currentOriginalOffset = 0

                for (match in chordRegex.findAll(originalText)) {
                    if (offset < match.range.first) {
                        return transformedOffset + (offset - currentOriginalOffset)
                    }
                    
                    // Add length of text before this chord
                    transformedOffset += (match.range.first - currentOriginalOffset)
                    
                    // If offset is inside the chord tag (inclusive start, inclusive end)
                    if (offset <= match.range.last + 1) {
                        val visualLength = getVisualLength(match.groupValues[1])
                        // If we are at the very end of the original chord tag, map to end of visual chord
                        if (offset == match.range.last + 1) {
                            return transformedOffset + visualLength
                        }
                        // Otherwise, map to start of visual chord (prevent cursor inside atomic chip)
                        return transformedOffset
                    }
                    
                    val visualLength = getVisualLength(match.groupValues[1])
                    transformedOffset += visualLength
                    currentOriginalOffset = match.range.last + 1
                }
                return transformedOffset + (offset - currentOriginalOffset)
            }

            override fun transformedToOriginal(offset: Int): Int {
                var currentTransformedOffset = 0
                var currentOriginalOffset = 0

                for (match in chordRegex.findAll(originalText)) {
                    val lenBefore = match.range.first - currentOriginalOffset
                    
                    if (offset < currentTransformedOffset + lenBefore) {
                        return currentOriginalOffset + (offset - currentTransformedOffset)
                    }
                    
                    currentTransformedOffset += lenBefore
                    currentOriginalOffset += lenBefore
                    
                    val visualLength = getVisualLength(match.groupValues[1])
                    
                    // If offset is within the visual chord
                    if (offset <= currentTransformedOffset + visualLength) {
                        // Map to the end of the original chord tag if at end of visual, else start?
                        // Usually map to start of original to select it whole, or if specific behavior needed.
                        // Standard behavior: if at end of visual, map to end of original.
                        if (offset == currentTransformedOffset + visualLength) {
                            return currentOriginalOffset + match.value.length
                        }
                        return currentOriginalOffset
                    }
                    
                    currentTransformedOffset += visualLength
                    currentOriginalOffset += match.value.length
                }
                return currentOriginalOffset + (offset - currentTransformedOffset)
            }
        }

        return TransformedText(annotatedString, offsetMapping)
    }

    private fun getVisualLength(chordName: String): Int {
        if (chordPlacement == songwritersnotebook.app.viewmodels.ChordPlacement.Above) return 1
        return when (displayMode) {
            ChordDisplayMode.Large -> chordName.length + 2
            ChordDisplayMode.Small -> chordName.length
            ChordDisplayMode.Off -> 0
        }
    }

    private fun hexToColor(hex: String): Color {
        return try {
            val colorString = hex.removePrefix("#")
            val argb = if (colorString.length == 6) {
                ("FF" + colorString).toLong(16)
            } else {
                colorString.toLong(16)
            }
            Color(argb)
        } catch (e: Exception) {
            Color.Unspecified
        }
    }
}
